/**
 * /account/usage — per-token request volume + rate-limit headroom.
 *
 * Pulls from /api/v1/usage/ which returns:
 *   {
 *     totals:  { requests_24h, requests_30d, last_used_at },
 *     daily:   [{date: "YYYY-MM-DD", count: int}, ...],   // last 30d
 *     hourly:  [{ts: ISO,            count: int}, ...],   // last 24h
 *     per_token: [{token_id, name, prefix, scopes, rate_limit_per_minute,
 *                  count_24h, count_30d, last_used_at}, ...]
 *   }
 */
import { useEffect, useMemo, useState } from "react";
import { Activity, Clock, BarChart3, AlertTriangle } from "lucide-react";
import api from "../../api/axios";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

function Spark({ data, height = 28 }) {
  if (!data?.length) return <div className="text-[10px] text-text-secondary">no data</div>;
  const max = Math.max(...data.map((d) => d.count || 0), 1);
  const w = 200, h = height;
  const step = w / Math.max(data.length - 1, 1);
  const pts = data.map((d, i) => {
    const x = i * step;
    const y = h - ((d.count || 0) / max) * (h - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={w} height={h} className="block">
      <polyline fill="none" stroke="currentColor" strokeWidth="1.4" points={pts} />
    </svg>
  );
}

function DailyBars({ data }) {
  if (!data?.length) return <div className="text-sm text-text-secondary">No traffic in the last 30 days.</div>;
  const max = Math.max(...data.map((d) => d.count || 0), 1);
  return (
    <div className="flex items-end gap-1 h-24">
      {data.map((d) => (
        <div key={d.date} title={`${d.date} — ${d.count}`}
             className="flex-1 bg-button-primary/60 hover:bg-button-primary rounded-t-sm transition-colors min-h-[2px]"
             style={{ height: `${((d.count || 0) / max) * 100}%` }} />
      ))}
    </div>
  );
}

export default function Usage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/api/v1/usage/");
        setData(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load usage data.");
      } finally { setLoading(false); }
    })();
  }, []);

  const tokens = data?.per_token || [];
  const totals = data?.totals || {};

  const headroomColor = (count24h, perMin) => {
    // Loose heuristic: if hitting >50% of (perMin * 60 * 24) over 24h, warn.
    const cap = (perMin || 60) * 60 * 24;
    const used = (count24h || 0) / cap;
    if (used > 0.8) return "text-red-400";
    if (used > 0.5) return "text-amber-400";
    return "text-emerald-400";
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="mb-5">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Activity className="w-5 h-5" /> API usage
        </h2>
        <p className="text-sm text-text-secondary mt-1">
          Request volume and rate-limit headroom across your tokens.
        </p>
      </div>

      {loading ? (
        <div className="text-text-secondary text-sm">Loading…</div>
      ) : err ? (
        <div className="bg-button-primary/10 border border-button-primary/30 rounded-lg px-3.5 py-2.5 text-sm text-button-primary">{err}</div>
      ) : (
        <>
          {/* Top-line totals */}
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            <div className="bg-bg-primary border border-border-default rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-text-secondary">Last 24h</div>
              <div className="text-2xl font-extrabold mt-1">{fmt(totals.requests_24h)}</div>
              <div className="text-[10px] text-text-secondary mt-1">requests</div>
            </div>
            <div className="bg-bg-primary border border-border-default rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-text-secondary">Last 30d</div>
              <div className="text-2xl font-extrabold mt-1">{fmt(totals.requests_30d)}</div>
              <div className="text-[10px] text-text-secondary mt-1">requests</div>
            </div>
            <div className="bg-bg-primary border border-border-default rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-text-secondary">Last used</div>
              <div className="text-base font-medium mt-1 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-text-secondary" />
                {totals.last_used_at ? new Date(totals.last_used_at).toLocaleString() : "—"}
              </div>
            </div>
          </div>

          {/* Daily bars */}
          <div className="bg-bg-primary border border-border-default rounded-xl p-4 mb-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold flex items-center gap-1.5">
                <BarChart3 className="w-4 h-4" /> Daily — last 30 days
              </div>
            </div>
            <DailyBars data={data?.daily} />
          </div>

          {/* Per-token */}
          <div className="bg-bg-primary border border-border-default rounded-xl overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-bg-secondary text-text-secondary text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left py-2 px-3">Token</th>
                  <th className="text-left py-2 px-3">Last 24h</th>
                  <th className="text-left py-2 px-3">Last 30d</th>
                  <th className="text-left py-2 px-3">Headroom</th>
                  <th className="text-left py-2 px-3 text-button-primary">Trend</th>
                </tr>
              </thead>
              <tbody>
                {tokens.length === 0 ? (
                  <tr><td colSpan={5} className="py-6 text-center text-text-secondary">No tokens issued yet.</td></tr>
                ) : tokens.map((t) => (
                  <tr key={t.token_id} className="border-t border-border-default">
                    <td className="py-2 px-3">
                      <div className="font-medium">{t.name}</div>
                      <div className="font-mono text-[10px] text-text-secondary">{t.prefix}…</div>
                    </td>
                    <td className="py-2 px-3">{fmt(t.count_24h)}</td>
                    <td className="py-2 px-3">{fmt(t.count_30d)}</td>
                    <td className={`py-2 px-3 ${headroomColor(t.count_24h, t.rate_limit_per_minute)}`}>
                      {t.rate_limit_per_minute}/min
                      {((t.count_24h || 0) / ((t.rate_limit_per_minute || 60) * 60 * 24)) > 0.8 && (
                        <AlertTriangle className="w-3.5 h-3.5 inline ml-1" />
                      )}
                    </td>
                    <td className="py-2 px-3 text-button-primary">
                      <Spark data={t.daily || []} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
